"use client";

import { Check, Palette, RotateCcw } from "lucide-react";
import type { BrandTheme } from "@workspace/ui/brand-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { Button } from "@workspace/ui/components/button";
import { useBrandTheme } from "@workspace/ui/hooks/useBrandTheme";

export interface BrandThemeSwitcherProps {
  // URL of the editor app/route — typically https://design-system.l.gaz.codes/themes
  editorHref?: string;
}

// Pull a representative swatch color from a preset for the dropdown dot.
// Prefers primary, falls back through accent, foreground, background.
function swatchColor(preset: BrandTheme): { bg: string; border: string } {
  const lightColors = preset.modes.light?.colors ?? preset.modes.dark?.colors;
  const darkColors = preset.modes.dark?.colors ?? preset.modes.light?.colors;
  const colors = lightColors ?? darkColors ?? {};
  return {
    bg: colors.primary ?? colors.accent ?? colors.foreground ?? colors.background ?? "transparent",
    border: colors.border ?? "rgba(0,0,0,0.2)",
  };
}

function modeBadge(preset: BrandTheme): "light" | "dark" | "both" {
  const hasLight = !!preset.modes.light;
  const hasDark = !!preset.modes.dark;
  if (hasLight && hasDark) return "both";
  return hasDark ? "dark" : "light";
}

export function BrandThemeSwitcher({ editorHref }: BrandThemeSwitcherProps) {
  const { activeTheme, presets, setTheme } = useBrandTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-2 px-2 text-xs"
          aria-label="Brand theme"
        >
          <Palette className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{activeTheme?.name ?? "Themes"}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Brand themes</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {presets.map((preset) => {
          const isActive = preset.slug === activeTheme?.slug;
          const swatch = swatchColor(preset);
          const badge = modeBadge(preset);
          return (
            <DropdownMenuItem
              key={preset.slug}
              onClick={() => setTheme(preset.slug)}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-3 w-3 rounded-full border"
                  style={{ background: swatch.bg, borderColor: swatch.border }}
                  aria-hidden
                />
                {preset.name}
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  {badge === "both" ? "L/D" : badge.charAt(0).toUpperCase()}
                </span>
              </span>
              {isActive ? <Check className="h-3.5 w-3.5" /> : null}
            </DropdownMenuItem>
          );
        })}
        {activeTheme ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setTheme(null)}
              className="text-muted-foreground"
            >
              <RotateCcw className="mr-2 h-3.5 w-3.5" />
              Reset to default
            </DropdownMenuItem>
          </>
        ) : null}
        {editorHref ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href={editorHref} target="_blank" rel="noreferrer">
                Open editor →
              </a>
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
